import { useEffect, useState } from 'react';
import { fetchBanner } from 'service/service';

interface BannerContent {
  id?: string;
  bannerId?: string;
  title?: string | null;
  text: string;
  type?: string;
  severity?: 'error' | 'info' | 'success' | 'warning';
  contentType?: string;
  isGlobal?: boolean;
  dashboardId?: number | string;
  dashboardName?: string;
  dashboardUrl?: string;
  banners?: any[];
}

interface UseBannerOptions {
  dashboardId?: number;
  dashboardName?: string;
  dashboardUrl?: string;
}

export const useBanner = ({ dashboardId, dashboardName, dashboardUrl }: UseBannerOptions) => {
  const [banner, setBanner] = useState<BannerContent | null>(null);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    let isActive = true;
    setBanner(null);
    setIsDismissed(false);

    const resolvedUrl = dashboardUrl || (typeof window !== 'undefined' ? window.location.pathname : '');

    fetchBanner({ dashboardUrl: resolvedUrl })
      .then((response) => {
        const bannerData = response?.data ?? response;
        if (!isActive || !bannerData) return;

        const bannerList: any[] = Array.isArray(bannerData.banners) && bannerData.banners.length > 0
          ? bannerData.banners
          : (typeof bannerData.text === 'string' && bannerData.text.length > 0 ? [bannerData] : []);

        const unDismissed = bannerList.find((b: any) => {
          const id = b.bannerId || b.id;
          return !id || typeof window === 'undefined' || localStorage.getItem(`pepwise_banner_dismissed_${id}`) !== '1';
        });

        if (unDismissed) {
          const mappedSeverity = unDismissed.type === 'critical' ? 'error' : (unDismissed.type || 'info');
          setBanner({
            id: unDismissed.bannerId || unDismissed.id,
            bannerId: unDismissed.bannerId || unDismissed.id,
            title: unDismissed.bannerTitle || unDismissed.title || null,
            text: unDismissed.bannerText || unDismissed.text || unDismissed.message || '',
            type: unDismissed.type || 'info',
            severity: mappedSeverity,
            contentType: mappedSeverity,
            isGlobal: unDismissed.isGlobal ?? bannerData.isGlobal ?? false,
            dashboardId: unDismissed.dashboardId || bannerData.dashboardId,
            dashboardName: unDismissed.dashboardName || bannerData.dashboardName,
            dashboardUrl: unDismissed.dashboardUrl || bannerData.dashboardUrl,
            banners: bannerList,
          });
          setIsDismissed(false);
        } else {
          setIsDismissed(true);
        }
      })
      .catch((error) => {
        if (isActive) {
          console.error('Failed to load banner:', error);
        }
      });

    return () => {
      isActive = false;
    };
  }, [dashboardId, dashboardName, dashboardUrl]);

  const handleDismiss = () => {
    const id = banner?.bannerId || banner?.id;
    if (id && typeof window !== 'undefined') {
      localStorage.setItem(`pepwise_banner_dismissed_${id}`, '1');
    }

    const remaining = (banner?.banners || []).find((b: any) => {
      const bId = b.bannerId || b.id;
      return bId && bId !== id && localStorage.getItem(`pepwise_banner_dismissed_${bId}`) !== '1';
    });

    if (remaining) {
      const mappedSeverity = remaining.type === 'critical' ? 'error' : (remaining.type || 'info');
      setBanner({
        id: remaining.bannerId || remaining.id,
        bannerId: remaining.bannerId || remaining.id,
        title: remaining.bannerTitle || remaining.title || null,
        text: remaining.bannerText || remaining.text || remaining.message || '',
        type: remaining.type || 'info',
        severity: mappedSeverity,
        contentType: mappedSeverity,
        isGlobal: remaining.isGlobal ?? false,
        dashboardId: remaining.dashboardId,
        dashboardName: remaining.dashboardName,
        dashboardUrl: remaining.dashboardUrl,
        banners: banner?.banners,
      });
    } else {
      setIsDismissed(true);
    }
  };

  return {
    banner,
    isVisible: Boolean(banner && !isDismissed),
    dismiss: handleDismiss,
  };
};